/**
 * ChatDrawer.jsx — Stable Talk
 * Four-tab social hub: Lounge (public), Friends (private), Teams, Requests
 */

import { useState, useEffect, useRef } from 'react';
import { useChatDrawer } from '../../context/ChatDrawerContext';
import { db, auth, functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection, query, orderBy, limit, where,
  onSnapshot, addDoc, setDoc, getDocs, getDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

// ── Lounge Tab ─────────────────────────────────────────────────────────────────

function LoungeTab({ currentUser, displayName }) {
  const [messages, setMessages] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [sending,  setSending]  = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const q = query(
      collection(db, 'lounge_messages'),
      orderBy('createdAt', 'asc'),
      limit(50)
    );
    return onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  async function sendMessage() {
    const text = inputVal.trim();
    if (!text || !currentUser || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'lounge_messages'), {
        text,
        userId:      currentUser.uid,
        displayName: displayName || 'Trainer',
        createdAt:   serverTimestamp(),
      });
      setInputVal('');
    } catch (e) {
      console.error('[Stable Talk] Send failed:', e);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div className="st-tab-content">
      <div className="st-lounge-header">
        <span className="st-lounge-header__icon">🌐</span>
        <span className="st-lounge-header__text">Public · All players can see this</span>
      </div>

      <div className="st-messages">
        {messages.length === 0 && (
          <div className="st-empty">
            <p className="st-empty__icon">🏇</p>
            <p className="st-empty__text">No messages yet — start the conversation!</p>
          </div>
        )}
        {messages.map(msg => {
          const isSelf = msg.userId === currentUser?.uid;
          return (
            <div key={msg.id} className={`st-message${isSelf ? ' st-message--self' : ''}`}>
              {!isSelf && (
                <div className="st-message__avatar">
                  {(msg.displayName || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="st-message__wrap">
                {!isSelf && (
                  <span className="st-message__name">{msg.displayName}</span>
                )}
                <div className="st-message__bubble">{msg.text}</div>
                <span className="st-message__time">
                  {msg.createdAt?.toDate
                    ? msg.createdAt.toDate().toLocaleTimeString('en-AU', {
                        hour: 'numeric', minute: '2-digit', hour12: true,
                      })
                    : 'now'}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="st-input-row">
        <textarea
          className="st-input"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message the Lounge…"
          rows={1}
          disabled={!currentUser}
        />
        <button
          className="st-send-btn"
          onClick={sendMessage}
          disabled={!inputVal.trim() || sending}
          aria-label="Send message"
        >
          ➤
        </button>
      </div>
    </div>
  );
}

// ── sendPrivateMessage helper ───────────────────────────────────────────────────
// Creates the conversation doc with participantMap (avoids race-condition get()
// in Firestore rules), then writes the message.

async function sendPrivateMessage(currentUid, otherUid, text) {
  if (!text.trim()) return;
  const ids            = [currentUid, otherUid].sort();
  const conversationId = ids.join('_');

  const [blockedByMe, blockedMe, friendSnap] = await Promise.all([
    getDoc(doc(db, 'blocks', currentUid, 'list', otherUid)),
    getDoc(doc(db, 'blocks', otherUid,   'list', currentUid)),
    getDoc(doc(db, 'friends', currentUid, 'list', otherUid)),
  ]);
  if (blockedByMe.exists() || blockedMe.exists()) throw new Error('BLOCKED');
  if (!friendSnap.exists()) throw new Error('NOT_FRIENDS');

  const convoRef  = doc(db, 'conversations', conversationId);
  const convoSnap = await getDoc(convoRef);

  if (!convoSnap.exists()) {
    // Pure create — no merge ambiguity; rules evaluate this as allow create
    await setDoc(convoRef, {
      participants:   ids,
      participantMap: { [ids[0]]: true, [ids[1]]: true },
      type:           'private',
      createdAt:      serverTimestamp(),
      updatedAt:      serverTimestamp(),
      lastMessage:    null,
      lastMessageAt:  null,
      lastSenderId:   null,
    });
  }

  await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
    userId:    currentUid,
    text:      text.trim(),
    createdAt: serverTimestamp(),
    readBy:    { [currentUid]: true },
  });

  // Pure update — doc is now guaranteed to exist
  await updateDoc(convoRef, {
    lastMessage:   text.trim(),
    lastMessageAt: serverTimestamp(),
    lastSenderId:  currentUid,
    updatedAt:     serverTimestamp(),
  });
}

// ── Friends Tab ────────────────────────────────────────────────────────────────

function FriendsTab({ currentUser }) {
  const [friends,      setFriends]      = useState([]);
  const [blocked,      setBlocked]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeConvo,  setActiveConvo]  = useState(null);
  const [openMenuUid,  setOpenMenuUid]  = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('friends');

  // Close menu on outside click
  useEffect(() => {
    if (!openMenuUid) return;
    const close = () => setOpenMenuUid(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuUid]);

  // Subscribe to friends/{uid}/list subcollection
  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(
      collection(db, 'friends', currentUser.uid, 'list'),
      async snap => {
        const profiles = await Promise.all(
          snap.docs.map(async d => {
            try {
              const p = await getDoc(doc(db, 'player_profiles', d.data().uid));
              return p.exists() ? { uid: d.data().uid, ...p.data() } : null;
            } catch { return null; }
          })
        );
        setFriends(profiles.filter(Boolean));
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [currentUser]);

  // Subscribe to blocks/{uid}/list subcollection
  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(
      collection(db, 'blocks', currentUser.uid, 'list'),
      async snap => {
        const profiles = await Promise.all(
          snap.docs.map(async d => {
            try {
              const p = await getDoc(doc(db, 'player_profiles', d.data().blockedUid));
              return p.exists()
                ? { uid: d.data().blockedUid, ...p.data() }
                : { uid: d.data().blockedUid, username: d.data().blockedUsername || 'Unknown', displayName: '' };
            } catch { return null; }
          })
        );
        setBlocked(profiles.filter(Boolean));
      },
      () => {}
    );
  }, [currentUser]);

  async function openChat(friend) {
    try {
      const [blockedByMe, blockedMe] = await Promise.all([
        getDoc(doc(db, 'blocks', currentUser.uid, 'list', friend.uid)),
        getDoc(doc(db, 'blocks', friend.uid, 'list', currentUser.uid)),
      ]);
      if (blockedByMe.exists() || blockedMe.exists()) {
        alert('You cannot message this user.');
        return;
      }

      const ids            = [currentUser.uid, friend.uid].sort();
      const conversationId = ids.join('_');
      const convoRef       = doc(db, 'conversations', conversationId);

      // ── DIAGNOSTIC ────────────────────────────────────────────────
      console.log('[DEBUG] auth.currentUser?.uid:', auth.currentUser?.uid);
      console.log('[DEBUG] currentUser.uid (prop):', currentUser.uid);
      console.log('[DEBUG] ids:', ids);
      console.log('[DEBUG] conversationId:', conversationId);
      console.log('[DEBUG] payload participantMap:', { [ids[0]]: true, [ids[1]]: true });
      // ─────────────────────────────────────────────────────────────

      const convoSnap = await getDoc(convoRef);
      console.log('[DEBUG] convoSnap.exists():', convoSnap.exists());

      if (!convoSnap.exists()) {
        const payload = {
          participants:   ids,
          participantMap: { [ids[0]]: true, [ids[1]]: true },
          type:           'private',
          createdAt:      serverTimestamp(),
          updatedAt:      serverTimestamp(),
          lastMessage:    null,
          lastMessageAt:  null,
        };
        console.log('[DEBUG] About to setDoc with payload:', payload);
        await setDoc(convoRef, payload);
        console.log('[DEBUG] setDoc SUCCEEDED');
      }

      setActiveConvo({ ...friend, convoId: conversationId });
    } catch (e) {
      console.error('[FriendsTab] openChat error:', e.code, e.message);
      console.error('[DEBUG] Full error object:', e);
    }
  }

  async function unfriendSilent(friend) {
    try {
      await Promise.all([
        deleteDoc(doc(db, 'friends', currentUser.uid, 'list', friend.uid)),
        deleteDoc(doc(db, 'friends', friend.uid,      'list', currentUser.uid)),
      ]);
      const q1 = query(collection(db, 'friend_requests'),
        where('fromUserId', '==', currentUser.uid), where('toUserId', '==', friend.uid));
      const q2 = query(collection(db, 'friend_requests'),
        where('fromUserId', '==', friend.uid), where('toUserId', '==', currentUser.uid));
      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      await Promise.all([...s1.docs, ...s2.docs].map(d => updateDoc(d.ref, { status: 'blocked' })));
    } catch {}
  }

  async function unfriend(friend) {
    if (!window.confirm(`Unfriend @${friend.username}?`)) return;
    try {
      await Promise.all([
        deleteDoc(doc(db, 'friends', currentUser.uid, 'list', friend.uid)),
        deleteDoc(doc(db, 'friends', friend.uid,      'list', currentUser.uid)),
      ]);
      const q1 = query(collection(db, 'friend_requests'),
        where('fromUserId', '==', currentUser.uid), where('toUserId', '==', friend.uid));
      const q2 = query(collection(db, 'friend_requests'),
        where('fromUserId', '==', friend.uid), where('toUserId', '==', currentUser.uid));
      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      await Promise.all([...s1.docs, ...s2.docs].map(d => updateDoc(d.ref, { status: 'removed' })));
    } catch (e) { console.error('[FriendsTab] unfriend error:', e.code, e.message); }
  }

  async function blockUser(friend) {
    if (!window.confirm(
      `Block @${friend.username}? This will be flagged to our admin team.`
    )) return;
    try {
      await setDoc(doc(db, 'blocks', currentUser.uid, 'list', friend.uid), {
        blockedUid:      friend.uid,
        blockedUsername: friend.username || '',
        createdAt:       serverTimestamp(),
      });
      await unfriendSilent(friend);
      await addDoc(collection(db, 'admin_flags'), {
        type:             'block',
        reporterUid:      currentUser.uid,
        reporterUsername: currentUser.displayName || '',
        flaggedUid:       friend.uid,
        flaggedUsername:  friend.username || '',
        context:          'Blocked via Stable Talk',
        status:           'pending',
        createdAt:        serverTimestamp(),
      });
    } catch (e) { console.error('[FriendsTab] blockUser error:', e.code, e.message); }
  }

  async function unblock(blockedUser) {
    try {
      await deleteDoc(doc(db, 'blocks', currentUser.uid, 'list', blockedUser.uid));
    } catch (e) { console.error('[FriendsTab] unblock error:', e); }
  }

  if (activeConvo) {
    return (
      <PrivateChat
        convo={activeConvo}
        currentUser={currentUser}
        onBack={() => setActiveConvo(null)}
        sendMessage={sendPrivateMessage}
      />
    );
  }

  // ── Shared inline styles ────────────────────────────────────────────────────
  const menuBtn = {
    background: 'none', border: 'none', color: '#73859A',
    cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700,
    padding: '4px 8px', borderRadius: 4,
  };
  const menuItem = {
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', background: 'none', border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    color: '#B4C2D3', fontSize: '0.85rem', fontWeight: 600,
    padding: '13px 16px', cursor: 'pointer', textAlign: 'left',
    fontFamily: 'inherit',
  };
  const menuItemDanger = { ...menuItem, color: '#f87171', borderBottom: 'none' };
  const avatarBase = {
    width: 36, height: 36, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: '0.85rem', flexShrink: 0, position: 'relative',
  };
  const rowBase = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  };
  const dropdown = {
    position: 'absolute', right: 14, top: 'calc(100% - 4px)',
    zIndex: 9999, background: '#0d1b2e',
    border: '1px solid rgba(94,143,201,0.4)', borderRadius: 10,
    boxShadow: '0 8px 32px rgba(0,0,0,0.7)', minWidth: 190, overflow: 'hidden',
  };

  return (
    <div className="st-tab-content">
      {/* Sub-tabs: Friends | Blocked */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        {['friends', 'blocked'].map(sub => (
          <button key={sub} onClick={() => setActiveSubTab(sub)} style={{
            flex: 1, background: 'none', border: 'none',
            borderBottom: activeSubTab === sub ? '2px solid #126DFF' : '2px solid transparent',
            color: activeSubTab === sub ? '#126DFF' : '#73859A',
            fontSize: '0.75rem', fontWeight: 700, padding: '10px 4px',
            cursor: 'pointer', letterSpacing: '0.04em', fontFamily: 'inherit',
          }}>
            {sub === 'friends' ? `Friends (${friends.length})` : `Blocked (${blocked.length})`}
          </button>
        ))}
      </div>

      {/* ── Friends sub-tab ─────────────────────────────────────────────── */}
      {activeSubTab === 'friends' && (
        loading ? (
          <div className="st-empty"><p className="st-empty__text">Loading…</p></div>
        ) : friends.length === 0 ? (
          <div className="st-empty">
            <p className="st-empty__icon">👥</p>
            <p className="st-empty__text">No friends yet</p>
            <p className="st-empty__sub">Send requests from the Requests tab</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {friends.map(f => (
              <div key={f.uid} style={{ position: 'relative' }}>
                <div style={{ ...rowBase, cursor: 'pointer' }} onClick={() => openChat(f)}>
                  <div style={{ ...avatarBase, background: 'rgba(18,109,255,0.2)', color: '#126DFF' }}>
                    {(f.username || '?')[0].toUpperCase()}
                    <span style={{
                      position: 'absolute', bottom: 0, right: 0,
                      width: 9, height: 9, borderRadius: '50%',
                      background: '#22c55e', border: '2px solid #0d1b2e',
                    }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
                      @{f.username || f.displayName}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#73859A' }}>{f.displayName}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button style={menuBtn} onClick={() => openChat(f)} title="Message">💬</button>
                    <button style={menuBtn}
                      onClick={e => {
                        e.stopPropagation();
                        setOpenMenuUid(prev => prev === f.uid ? null : f.uid);
                      }}
                      title="More"
                    >•••</button>
                  </div>
                </div>

                {openMenuUid === f.uid && (
                  <div style={dropdown} onClick={e => e.stopPropagation()}>
                    <button style={menuItem} onClick={() => { setOpenMenuUid(null); openChat(f); }}>
                      💬 Send Message
                    </button>
                    <button style={menuItem} onClick={() => { setOpenMenuUid(null); unfriend(f); }}>
                      👋 Unfriend
                    </button>
                    <button style={menuItemDanger} onClick={() => { setOpenMenuUid(null); blockUser(f); }}>
                      🚫 Block & Report
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Blocked sub-tab ─────────────────────────────────────────────── */}
      {activeSubTab === 'blocked' && (
        blocked.length === 0 ? (
          <div className="st-empty">
            <p className="st-empty__icon">🚫</p>
            <p className="st-empty__text">No blocked users</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {blocked.map(u => (
              <div key={u.uid} style={rowBase}>
                <div style={{ ...avatarBase, background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>
                  {(u.username || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
                    @{u.username}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#73859A' }}>{u.displayName}</div>
                </div>
                <button onClick={() => unblock(u)} style={{
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                  color: '#f87171', borderRadius: 6, fontSize: '0.75rem', fontWeight: 700,
                  padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ── Private Chat ───────────────────────────────────────────────────────────────

function PrivateChat({ convo, currentUser, onBack, sendMessage }) {
  const [messages,     setMessages]     = useState([]);
  const [inputVal,     setInputVal]     = useState('');
  const [sending,      setSending]      = useState(false);
  const [blocked,      setBlocked]      = useState(false);
  const [blockReason,  setBlockReason]  = useState('');
  const bottomRef = useRef(null);

  // Check block state in both directions
  useEffect(() => {
    if (!currentUser || !convo.uid) return;
    Promise.all([
      getDoc(doc(db, 'blocks', currentUser.uid, 'list', convo.uid)),
      getDoc(doc(db, 'blocks', convo.uid,       'list', currentUser.uid)),
    ]).then(([byMe, byThem]) => {
      if (byMe.exists()) {
        setBlocked(true); setBlockReason('You have blocked this user.');
      } else if (byThem.exists()) {
        setBlocked(true); setBlockReason('You cannot message this user.');
      }
    });
  }, [currentUser, convo.uid]);

  // Subscribe to messages
  useEffect(() => {
    if (!convo.convoId) return;
    const q = query(
      collection(db, 'conversations', convo.convoId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(50)
    );
    return onSnapshot(q, snap =>
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [convo.convoId]);

  // Mark as read when opened
  useEffect(() => {
    if (!currentUser || !convo.convoId) return;
    updateDoc(doc(db, 'conversations', convo.convoId), {
      [`lastReadAt.${currentUser.uid}`]: serverTimestamp(),
    }).catch(() => {});
  }, [convo.convoId, currentUser]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  async function handleSend() {
    const text = inputVal.trim();
    if (!text || !currentUser || sending || blocked) return;
    setSending(true);
    try {
      await sendMessage(currentUser.uid, convo.uid, text);
      setInputVal('');
    } catch (e) {
      if (e.message === 'BLOCKED') {
        setBlocked(true); setBlockReason('Messaging is blocked between you and this user.');
      } else if (e.message === 'NOT_FRIENDS') {
        setBlocked(true); setBlockReason('You are no longer friends with this user.');
      } else {
        console.error('[PrivateChat] send error:', e.code, e.message);
      }
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  return (
    <div className="st-tab-content">
      <div className="st-private-header">
        <button className="st-private-header__back" onClick={onBack}>← Back</button>
        <div className="st-private-header__info">
          <span className="st-private-header__name">@{convo.username || convo.displayName}</span>
          <span className="st-private-header__sub">{blocked ? '🚫 Blocked' : 'Private message'}</span>
        </div>
      </div>

      <div className="st-messages">
        {messages.length === 0 && !blocked && (
          <div className="st-empty">
            <p className="st-empty__icon">💬</p>
            <p className="st-empty__text">Say hello to @{convo.username}</p>
          </div>
        )}
        {messages.map(msg => {
          const isSelf = msg.userId === currentUser?.uid;
          return (
            <div key={msg.id} className={`st-message${isSelf ? ' st-message--self' : ''}`}>
              {!isSelf && (
                <div className="st-message__avatar">
                  {(convo.username || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="st-message__wrap">
                {!isSelf && <span className="st-message__name">@{convo.username}</span>}
                <div className="st-message__bubble">{msg.text}</div>
                <span className="st-message__time">
                  {msg.createdAt?.toDate
                    ? msg.createdAt.toDate().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
                    : 'now'}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {blocked ? (
        <div style={{
          padding: '12px 14px', background: 'rgba(239,68,68,0.08)',
          borderTop: '1px solid rgba(239,68,68,0.2)',
          fontSize: '0.78rem', color: '#f87171', textAlign: 'center', flexShrink: 0,
        }}>
          {blockReason}
        </div>
      ) : (
        <div className="st-input-row">
          <textarea
            className="st-input"
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message @${convo.username}…`}
            rows={1}
          />
          <button className="st-send-btn" onClick={handleSend}
            disabled={!inputVal.trim() || sending} aria-label="Send">➤
          </button>
        </div>
      )}
    </div>
  );
}

// ── Teams Tab ──────────────────────────────────────────────────────────────────

function TeamsTab({ currentUser }) {
  const [teams,      setTeams]      = useState([]);
  const [friends,    setFriends]    = useState([]);
  const [creating,   setCreating]   = useState(false);
  const [groupName,  setGroupName]  = useState('');
  const [selected,   setSelected]   = useState(new Set());
  const [activeTeam, setActiveTeam] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(
      query(collection(db, 'teams'), where('members', 'array-contains', currentUser.uid)),
      snap => setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !showCreate) return;
    Promise.all([
      getDocs(query(collection(db, 'friend_requests'),
        where('fromUserId', '==', currentUser.uid), where('status', '==', 'accepted'))),
      getDocs(query(collection(db, 'friend_requests'),
        where('toUserId',   '==', currentUser.uid), where('status', '==', 'accepted'))),
    ]).then(async ([s1, s2]) => {
      const uids = [
        ...s1.docs.map(d => d.data().toUserId),
        ...s2.docs.map(d => d.data().fromUserId),
      ].filter(uid => uid !== currentUser.uid);
      const profiles = await Promise.all(
        uids.map(async uid => {
          try {
            const snap = await getDoc(doc(db, 'player_profiles', uid));
            return snap.exists() ? { uid, ...snap.data() } : null;
          } catch { return null; }
        })
      );
      setFriends(profiles.filter(Boolean));
    });
  }, [currentUser, showCreate]);

  async function createGroup() {
    if (!groupName.trim() || selected.size === 0) return;
    setCreating(true);
    try {
      const members = [currentUser.uid, ...Array.from(selected)];
      const ref = await addDoc(collection(db, 'teams'), {
        name:        groupName.trim(),
        members,
        createdBy:   currentUser.uid,
        createdAt:   serverTimestamp(),
        lastMessage: null,
      });
      setShowCreate(false);
      setGroupName('');
      setSelected(new Set());
      setActiveTeam({ id: ref.id, name: groupName.trim(), members });
    } catch (e) {
      console.error('[Stable Talk] Create group error:', e);
    } finally {
      setCreating(false);
    }
  }

  function toggleSelect(uid) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }

  if (activeTeam) {
    return (
      <TeamChat
        team={activeTeam}
        currentUser={currentUser}
        onBack={() => setActiveTeam(null)}
        onLeave={() => setActiveTeam(null)}
      />
    );
  }

  if (showCreate) {
    return (
      <div className="st-tab-content" style={{ overflowY: 'auto' }}>
        <button className="st-private-header__back" style={{ padding: '12px 14px', display: 'block' }} onClick={() => setShowCreate(false)}>
          ← Back
        </button>
        <div style={{ padding: '0 14px 14px' }}>
          <p className="st-requests-section__label">GROUP NAME</p>
          <input
            className="st-requests-input"
            placeholder="e.g. Queensland Crushers"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
          />
          <p className="st-requests-section__label" style={{ marginTop: 14 }}>
            ADD FRIENDS ({selected.size} selected)
          </p>
          {friends.length === 0 ? (
            <p className="st-requests-no-results">No friends yet — add friends from the Requests tab first</p>
          ) : friends.map(f => (
            <div
              key={f.uid}
              className={`st-search-result${selected.has(f.uid) ? ' st-search-result--selected' : ''}`}
              onClick={() => toggleSelect(f.uid)}
              style={{ marginBottom: 4 }}
            >
              <div className="st-search-result__avatar">{(f.username || '?')[0].toUpperCase()}</div>
              <div className="st-search-result__info">
                <span className="st-search-result__username">@{f.username}</span>
                <span className="st-search-result__name">{f.displayName}</span>
              </div>
              {selected.has(f.uid) && <span className="st-search-result__check">✓</span>}
            </div>
          ))}
          <button
            className="st-requests-send-btn"
            style={{ opacity: (groupName.trim() && selected.size > 0) ? 1 : 0.4, marginTop: 16 }}
            disabled={!groupName.trim() || selected.size === 0 || creating}
            onClick={createGroup}
          >
            {creating ? 'Creating…' : `Create Group (${selected.size + 1} members)`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="st-tab-content">
      {teams.length === 0 ? (
        <div className="st-empty">
          <p className="st-empty__icon">🏇</p>
          <p className="st-empty__text">No group chats yet</p>
          <p className="st-empty__sub">Create a group with your friends below</p>
        </div>
      ) : (
        <div className="st-team-list">
          {teams.map(t => (
            <div key={t.id} className="st-team-row" onClick={() => setActiveTeam(t)}>
              <div className="st-team-row__icon">🏇</div>
              <div className="st-team-row__info">
                <span className="st-team-row__name">{t.name}</span>
                <span className="st-team-row__meta">
                  {t.members?.length || 0} members{t.lastMessage ? ` · ${t.lastMessage}` : ''}
                </span>
              </div>
              <span className="st-team-row__open-btn">›</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ padding: '12px 14px' }}>
        <button className="st-requests-send-btn" style={{ opacity: 1 }} onClick={() => setShowCreate(true)}>
          + Create Group Chat
        </button>
      </div>
    </div>
  );
}

// ── Team Chat ──────────────────────────────────────────────────────────────────

function TeamChat({ team, currentUser, onBack, onLeave }) {
  const [messages, setMessages] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [sending,  setSending]  = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bottomRef = useRef(null);

  // Close header menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  useEffect(() => {
    const q = query(
      collection(db, 'teams', team.id, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(50)
    );
    return onSnapshot(q, snap =>
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [team.id]);

  // Mark team as read when opened
  useEffect(() => {
    if (!currentUser || !team.id) return;
    updateDoc(doc(db, 'teams', team.id), {
      [`lastReadAt.${currentUser.uid}`]: serverTimestamp(),
    }).catch(() => {});
  }, [team.id, currentUser]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  async function sendMessage() {
    const text = inputVal.trim();
    if (!text || !currentUser || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'teams', team.id, 'messages'), {
        text,
        userId:      currentUser.uid,
        displayName: currentUser.displayName || 'Trainer',
        createdAt:   serverTimestamp(),
      });
      await updateDoc(doc(db, 'teams', team.id), {
        lastMessage:   text,
        lastMessageAt: serverTimestamp(),
        lastSenderId:  currentUser.uid,
      });
      setInputVal('');
    } catch (e) {
      console.error('[TeamChat] send error:', e);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  async function leaveTeam() {
    if (!window.confirm(`Leave ${team.name}? You'll need a new invite to rejoin.`)) return;
    try {
      const remaining = (team.members || []).filter(m => m !== currentUser.uid);
      await updateDoc(doc(db, 'teams', team.id), { members: remaining });
      setMenuOpen(false);
      onLeave?.();
    } catch (e) {
      console.error('[TeamChat] leaveTeam error:', e);
    }
  }

  async function reportTeam() {
    if (!window.confirm(`Report ${team.name} to admin? This flags the group for review.`)) return;
    try {
      await addDoc(collection(db, 'admin_flags'), {
        type:             'team_report',
        reporterUid:      currentUser.uid,
        reporterUsername: currentUser.displayName || '',
        teamId:           team.id,
        teamName:         team.name,
        context:          'Group chat reported via Stable Talk',
        status:           'pending',
        createdAt:        serverTimestamp(),
      });
      setMenuOpen(false);
      alert('Reported to our admin team. Thank you.');
    } catch (e) {
      console.error('[TeamChat] reportTeam error:', e);
    }
  }

  // Shared menu styles (matches FriendsTab pattern)
  const menuItemStyle = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    background: 'none', border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    color: '#B4C2D3', fontSize: '0.85rem', fontWeight: 600,
    padding: '13px 16px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  };

  return (
    <div className="st-tab-content">
      <div className="st-private-header" style={{ position: 'relative' }}>
        <button className="st-private-header__back" onClick={onBack}>← Back</button>
        <div className="st-private-header__info" style={{ flex: 1 }}>
          <span className="st-private-header__name">🏇 {team.name}</span>
          <span className="st-private-header__sub">{team.members?.length || 0} members</span>
        </div>
        <button
          onClick={e => { e.stopPropagation(); setMenuOpen(p => !p); }}
          style={{
            background: 'none', border: 'none', color: '#73859A',
            fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer',
            padding: '4px 8px', borderRadius: 4,
          }}
          title="More"
        >•••</button>

        {menuOpen && (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', right: 14, top: 'calc(100% - 4px)',
              zIndex: 9999, background: '#0d1b2e',
              border: '1px solid rgba(94,143,201,0.4)', borderRadius: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.7)', minWidth: 190, overflow: 'hidden',
            }}
          >
            <button style={menuItemStyle} onClick={leaveTeam}>
              👋 Leave Team
            </button>
            <button
              style={{ ...menuItemStyle, color: '#f87171', borderBottom: 'none' }}
              onClick={reportTeam}
            >
              🚫 Block &amp; Report
            </button>
          </div>
        )}
      </div>

      <div className="st-messages">
        {messages.length === 0 && (
          <div className="st-empty">
            <p className="st-empty__text">No messages yet — say hello!</p>
          </div>
        )}
        {messages.map(msg => {
          const isSelf = msg.userId === currentUser?.uid;
          return (
            <div key={msg.id} className={`st-message${isSelf ? ' st-message--self' : ''}`}>
              {!isSelf && (
                <div className="st-message__avatar">{(msg.displayName || '?')[0].toUpperCase()}</div>
              )}
              <div className="st-message__wrap">
                {!isSelf && <span className="st-message__name">{msg.displayName}</span>}
                <div className="st-message__bubble">{msg.text}</div>
                <span className="st-message__time">
                  {msg.createdAt?.toDate
                    ? msg.createdAt.toDate().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
                    : 'now'}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="st-input-row">
        <textarea
          className="st-input"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Message ${team.name}…`}
          rows={1}
        />
        <button className="st-send-btn" onClick={sendMessage}
          disabled={!inputVal.trim() || sending} aria-label="Send">➤
        </button>
      </div>
    </div>
  );
}

// ── Incoming Requests ──────────────────────────────────────────────────────────

function IncomingRequests({ currentUser }) {
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'friend_requests'),
      where('toUserId', '==', currentUser.uid),
      where('status',   '==', 'pending'),
      limit(10)
    );
    return onSnapshot(q, snap =>
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
  }, [currentUser]);

  async function respond(request, accept) {
    try {
      const fn = httpsCallable(
        functions,
        accept ? 'acceptFriendRequest' : 'declineFriendRequest'
      );
      await fn({ requestId: request.id });
    } catch (e) {
      console.error('[IncomingRequests] respond error:', e.code, e.message);
    }
  }

  if (requests.length === 0) {
    return <p className="st-requests-no-results">No pending requests</p>;
  }

  return (
    <div className="st-incoming-list">
      {requests.map(r => (
        <div key={r.id} className="st-incoming-row">
          <div className="st-incoming-row__info">
            <span className="st-incoming-row__from">@{r.fromUsername || r.fromUserId}</span>
            {r.message && (
              <span className="st-incoming-row__msg">"{r.message}"</span>
            )}
          </div>
          <div className="st-incoming-row__actions">
            <button className="st-incoming-accept"  onClick={() => respond(r, true)}>Accept</button>
            <button className="st-incoming-decline" onClick={() => respond(r, false)}>Decline</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Requests Tab ───────────────────────────────────────────────────────────────

function RequestsTab({ currentUser }) {
  const [searchVal,     setSearchVal]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [selectedUser,  setSelectedUser]  = useState(null);
  const [message,       setMessage]       = useState('');
  const [sendStatus,    setSendStatus]    = useState(null); // 'sent' | 'error'
  const searchTimeout = useRef(null);

  // Debounced search: usernames collection (no index needed) + displayName fallback
  useEffect(() => {
    const val = searchVal.trim();
    if (val.length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const clean = val.toLowerCase();

        // Primary: query usernames collection by document ID prefix — no index required
        const usernamesSnap = await getDocs(query(
          collection(db, 'usernames'),
          where('__name__', '>=', clean),
          where('__name__', '<=', clean + '\uf8ff'),
          limit(8)
        ));

        // Secondary: query users by displayName prefix (requires index, may be building)
        let nameSnap = { docs: [] };
        try {
          nameSnap = await getDocs(query(
            collection(db, 'users'),
            where('displayName', '>=', val),
            where('displayName', '<=', val + '\uf8ff'),
            limit(5)
          ));
        } catch {
          // Index still building — silently skip displayName search
        }

        console.log('[Stable Talk] usernames results:', usernamesSnap.docs.map(d => d.id));
        const usernameUids = usernamesSnap.docs
          .map(d => d.data().uid)
          .filter(uid => uid && uid !== currentUser?.uid);
        console.log('[Stable Talk] UIDs to fetch:', usernameUids);

        // Resolve UIDs → fetch from player_profiles (public) not users (private)
        const userSnaps = await Promise.all(
          usernameUids.map(async uid => {
            try {
              const snap = await getDoc(doc(db, 'player_profiles', uid));
              if (!snap.exists()) {
                console.warn('[Stable Talk] No player_profile for uid:', uid);
                return null;
              }
              return snap;
            } catch (e) {
              console.error('[Stable Talk] Failed to fetch player_profile for uid:', uid, e.code);
              return null;
            }
          })
        );

        const seen = new Set();
        const results = [];

        userSnaps.filter(Boolean).forEach(snap => {
          if (!seen.has(snap.id)) {
            seen.add(snap.id);
            results.push({ id: snap.id, ...snap.data() });
          }
        });

        console.log('[Stable Talk] Final results:', results.map(r => r.username));

        nameSnap.docs.forEach(d => {
          if (!seen.has(d.id) && d.id !== currentUser?.uid) {
            seen.add(d.id);
            results.push({ id: d.id, ...d.data() });
          }
        });

        // Filter out blocked users (both directions)
        try {
          const [blocksSnap, blockedBySnap] = await Promise.all([
            getDocs(query(collection(db, 'blocks'), where('blockerUid', '==', currentUser.uid))),
            getDocs(query(collection(db, 'blocks'), where('blockedUid', '==', currentUser.uid))),
          ]);
          const blockedUids = new Set([
            ...blocksSnap.docs.map(d => d.data().blockedUid),
            ...blockedBySnap.docs.map(d => d.data().blockerUid),
          ]);
          setSearchResults(results.filter(u => !blockedUids.has(u.id)));
        } catch {
          setSearchResults(results);
        }
      } catch (e) {
        console.error('[Stable Talk] Search error:', e);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [searchVal, currentUser]);

  async function sendRequest() {
    if (!selectedUser || !currentUser) return;
    try {
      await addDoc(collection(db, 'friend_requests'), {
        fromUserId:   currentUser.uid,
        fromUsername: currentUser.displayName || 'Trainer',
        toUserId:     selectedUser.id,
        message:      message.trim(),
        status:       'pending',
        createdAt:    serverTimestamp(),
      });
      setSendStatus('sent');
      setSelectedUser(null);
      setSearchVal('');
      setMessage('');
      setSearchResults([]);
    } catch (e) {
      console.error('[Stable Talk] Friend request error:', e);
      setSendStatus('error');
    }
  }

  return (
    <div className="st-tab-content" style={{ overflowY: 'auto' }}>

      {/* Search */}
      <div className="st-requests-section">
        <p className="st-requests-section__label">FIND A PLAYER</p>
        <input
          className="st-requests-input"
          type="text"
          placeholder="Search by username or name…"
          value={searchVal}
          onChange={e => { setSearchVal(e.target.value); setSelectedUser(null); setSendStatus(null); }}
        />

        {searching && (
          <div className="st-requests-results">
            <div className="st-requests-searching">Searching…</div>
          </div>
        )}
        {!searching && searchVal.trim().length >= 2 && searchResults.length === 0 && (
          <div className="st-requests-results">
            <div className="st-requests-no-results">No players found for "{searchVal}"</div>
          </div>
        )}
        {!searching && searchResults.length > 0 && (
          <div className="st-requests-results">
            {searchResults.map(u => (
              <div
                key={u.id}
                className={`st-search-result${selectedUser?.id === u.id ? ' st-search-result--selected' : ''}`}
                onClick={() => setSelectedUser(u)}
              >
                <div className="st-search-result__avatar">
                  {(u.username || u.displayName || '?')[0].toUpperCase()}
                </div>
                <div className="st-search-result__info">
                  <span className="st-search-result__username">@{u.username || '(no stable name)'}</span>
                  <span className="st-search-result__name">{u.displayName}</span>
                </div>
                {selectedUser?.id === u.id && (
                  <span className="st-search-result__check">✓</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send request form */}
      {selectedUser && (
        <div className="st-requests-section">
          <p className="st-requests-section__label">
            SEND REQUEST TO @{selectedUser.username || selectedUser.displayName}
          </p>
          <textarea
            className="st-requests-msg"
            placeholder={`Hi @${selectedUser.username || selectedUser.displayName}, want to connect on Stable Talk?`}
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
          />
          <button
            className="st-requests-send-btn"
            style={{ opacity: 1 }}
            onClick={sendRequest}
          >
            Send Friend Request →
          </button>
          {sendStatus === 'sent'  && <p className="st-requests-success">✓ Request sent!</p>}
          {sendStatus === 'error' && <p className="st-requests-error">Something went wrong. Try again.</p>}
        </div>
      )}

      {/* Incoming requests */}
      <div className="st-requests-section">
        <p className="st-requests-section__label">INCOMING REQUESTS</p>
        <IncomingRequests currentUser={currentUser} />
      </div>

    </div>
  );
}

// ── Main ChatDrawer ────────────────────────────────────────────────────────────

export default function ChatDrawer() {
  const {
    isOpen, close, toggle,
    activeTab, setActiveTab,
    friendsUnread, teamsUnread, requestsUnread,
    totalUnread, clearTab,
    isFabSuppressed,
  } = useChatDrawer();

  const [currentUser, setCurrentUser] = useState(null);
  const [displayName, setDisplayName] = useState('');

  useEffect(() => onAuthStateChanged(auth, u => {
    setCurrentUser(u);
    setDisplayName(u?.displayName || 'Trainer');
  }), []);

  // Track visual viewport height (accounts for iOS Safari keyboard + browser chrome)
  // Sets --visual-vh on :root so mobile panel height stays within visible area
  useEffect(() => {
    function setVh() {
      document.documentElement.style.setProperty(
        '--visual-vh',
        `${window.visualViewport?.height ?? window.innerHeight}px`
      );
    }
    setVh();
    window.visualViewport?.addEventListener('resize', setVh);
    window.addEventListener('resize', setVh);
    return () => {
      window.visualViewport?.removeEventListener('resize', setVh);
      window.removeEventListener('resize', setVh);
    };
  }, []);

  function handleTabChange(tabId) {
    setActiveTab(tabId);
    clearTab(tabId);
  }

  const tabs = [
    { id: 'lounge',   label: 'Lounge',   badge: 0               },
    { id: 'friends',  label: 'Friends',  badge: friendsUnread   },
    { id: 'teams',    label: 'Teams',    badge: teamsUnread     },
    { id: 'requests', label: 'Requests', badge: requestsUnread  },
  ];

  return (
    <>
      {/* Mobile overlay — click to close */}
      {isOpen && (
        <div className="st-overlay" onClick={close} />
      )}

      {/* Panel — always in DOM, shown/hidden via CSS opacity + pointer-events */}
      <div
        className={`st-panel${isOpen ? ' st-panel--open' : ''}`}
        role="dialog"
        aria-label="Stable Talk"
        aria-modal={isOpen ? 'true' : undefined}
      >
        {/* Header */}
        <div className="st-header">
          <div className="st-header__title">
            <span className="st-header__icon">🐎</span>
            <span className="st-header__name">Stable Talk</span>
          </div>
          <button className="st-header__close" onClick={close} aria-label="Close Stable Talk">
            ✕
          </button>
        </div>

        {/* Tab bar with per-tab unread badges */}
        <div className="st-tabs" role="tablist">
          {tabs.map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`st-tab${activeTab === tab.id ? ' st-tab--active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="st-tab__badge">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'lounge'   && <LoungeTab   currentUser={currentUser} displayName={displayName} />}
        {activeTab === 'friends'  && <FriendsTab  currentUser={currentUser} />}
        {activeTab === 'teams'    && <TeamsTab    currentUser={currentUser} />}
        {activeTab === 'requests' && <RequestsTab currentUser={currentUser} />}
      </div>

      {/* FAB — badge shows total unread across Friends + Teams + Requests.
          Suppressed (e.g. while the Stable Slip modal is open) so it never
          floats on top of / collides with another full-screen overlay. */}
      {!isFabSuppressed && (
      <button
        className="st-fab"
        onClick={toggle}
        aria-label={isOpen ? 'Close Stable Talk' : 'Open Stable Talk'}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <span className="st-fab__icon">✕</span>
        ) : (
          <>
            <span className="st-fab__icon">💬</span>
            {totalUnread > 0 && (
              <span className="st-fab__badge">
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </>
        )}
      </button>
      )}
    </>
  );
}
